import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useGetBulkUploadStatusById } from "../../api/services/bulkCreateProgram/bulkCreateProgramApis";
import {
  useGetBulkActivitiesResult,
  useSelectedParticipantMutation,
} from "../../api/services/bulkInsertActivity/bulkInsertApis";
import { SelectedParticipantPayload } from "../../api/services/bulkInsertActivity/models";
import {
  BulkInsertHeader,
  InformationModal,
  ITabs,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../components";
import {
  BULK_CATEGORY_FOR_STATUS,
  PAGE_IDS,
  PAGE_URLS,
  SummaryStatus,
} from "../../helpers/constants";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import { convertToUTCFormat, getValueOrZero } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import {
  bulkActivityListSelector,
  bulkActivitySelectedParticipantsSelector,
} from "../../redux/slices/program/programSelector";
import {
  setBulkActivityList,
  setBulkActivityProgramTemplateId,
  setBulkActivitySelectedParticipants,
} from "../../redux/slices/program/programSlice";
import { BulkUploadStatusResponse } from "../bulkCreateProgram/modelsAndSchema/Models";
import { CreateBulkActivityWrapper } from "./CreateBulkActivity.style";
import { CreateBulkActivityStepper } from "./components/createBulkActivityStepper/CreateBulkActivityStepper";
import BulkActivityDetails from "./components/steps/bulkActivityDetails/BulkActivityDetails";
import { BulkActivityProcess } from "./components/steps/bulkActivityProcess/BulkActivityProcess";
import {
  BulkActivitySearchParticipants,
  BulkActivitySearchParticipantsRef,
} from "./components/steps/bulkActivitySearchParticipants/BulkActivitySearchParticipants";
import { BulkSummaryDetails } from "./components/steps/bulkSummaryDetails/BulkSummaryDetails";
import { CreateBulkActivityFormKey } from "./modelsAndSchemas/Models";
import { CreateBulkActivitySchema } from "./modelsAndSchemas/ValidationSchema";
import { bulkActivitySiteCoreKeys } from "./siteCore/SiteCoreKeys";

const pageId = PAGE_IDS.CREATE_BULK_ACTIVITY;

interface CreateBulkActivityProps {
  status?: string;
  stepValue?: number;
  bulkImportId?: string;
  isFromManageBulkProcessFlow?: boolean;
}

const CreateBulkActivity: React.FC<CreateBulkActivityProps> = ({
  status,
  stepValue,
  bulkImportId,
  isFromManageBulkProcessFlow = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const { commonLabels, fetchLabel, commonValidationMessages } = useSiteCore();
  const [currentStep, setCurrentStep] = useState(stepValue ?? 0);
  const [showInformationModal, setShowInformationModal] = useState(false);
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [showSomethingWrongModal, setShowSomethingWrongModal] = useState(false);
  const [summaryData, setSummaryData] = useState<BulkUploadStatusResponse>(
    {} as BulkUploadStatusResponse,
  );
  const searchRef = useRef<BulkActivitySearchParticipantsRef>(null);

  const [refreshCount, setRefreshCount] = useState(0);

  const activityList = useSelector(bulkActivityListSelector);
  const filteredParticipants = useSelector(
    bulkActivitySelectedParticipantsSelector,
  );

  const informationMessage =
    currentStep === 0
      ? fetchLabel(
          bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
          bulkActivitySiteCoreKeys.atLeastSelectTwoParticipantsMessage,
        )
      : fetchLabel(
          bulkActivitySiteCoreKeys.searchParticipantsResultsKey,
          bulkActivitySiteCoreKeys.atLeastSelectOneActivityMessage,
        );
  const {
    mutateAsync: saveSelectedMutation,
    data: selectedParticipantData,
    isSuccess: isSelectedParticipantSuccess,
    isPending: isSelectedParticipantLoading,
    isError: isSelectedParticipantError,
  } = useSelectedParticipantMutation();

  const {
    isFetching: isRecordStatusLoading,
    data: recordStatus,
    isError: isRecordStatusError,
    isSuccess: isRecordStatusSuccess,
    refetchWithParams: refetchRecordStatus,
  } = useGetBulkUploadStatusById();

  const {
    isFetching: isBulkActivitiesResultLoading,
    data: bulkActivitiesResult,
    isError: isBulkActivitiesResultError,
    refetchWithParams: refetchBulkActivitiesResult,
  } = useGetBulkActivitiesResult();

  const defaultValues = {
    [CreateBulkActivityFormKey.bulkProcessName]: "",
  };

  const createBulkActivityMethods = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(CreateBulkActivitySchema(commonValidationMessages)),
  });

  const { handleSubmit, getValues, setValue } = createBulkActivityMethods;

  const tabIdOne = `${pageId}-tab1`;
  const tabIdTwo = `${pageId}-tab2`;
  const tabIdThree = `${pageId}-tab3`;
  const tabs: ITabs[] = [
    {
      id: tabIdOne,
      label: "",
    },
    {
      id: tabIdTwo,
      label: "",
    },
    {
      id: tabIdThree,
      label: "",
    },
  ];

  const selectedParticipants = useMemo(
    () => filteredParticipants.filter((participant) => participant.isSelected),
    [filteredParticipants],
  );

  useEffect(() => {
    if (
      isSelectedParticipantError ||
      isRecordStatusError ||
      isBulkActivitiesResultError
    ) {
      setShowSomethingWrongModal(true);
    }
  }, [
    isSelectedParticipantError,
    isRecordStatusError,
    isBulkActivitiesResultError,
  ]);

  // Set summary data from props (existing useEffects will trigger API calls)
  useEffect(() => {
    if (bulkImportId && status) {
      setSummaryData((prev) => ({
        ...prev,
        bulkImportId: bulkImportId,
        status: status,
      }));
    }
  }, [bulkImportId, status]);

  // Sync stepValue prop with currentStep state
  useEffect(() => {
    if (stepValue !== undefined) {
      setCurrentStep(stepValue);
    }
  }, [stepValue]);

  useEffect(() => {
    if (status === SummaryStatus.COMPLETED && bulkImportId) {
      refetchBulkActivitiesResult(bulkImportId);
    }
  }, [status, bulkImportId]);

  const handleRecordStatusSuccess = (
    recordStatus: BulkUploadStatusResponse,
  ) => {
    const { bulkProcessName } = getValues();
    setSummaryData((prev) => ({
      ...prev,
      ...recordStatus,
      processName: bulkProcessName,
    }));

    if (recordStatus.status === SummaryStatus.COMPLETED) {
      setCurrentStep(2);
      refetchBulkActivitiesResult(recordStatus.bulkImportId);
    }
  };

  useEffect(() => {
    if (isRecordStatusSuccess) {
      handleRecordStatusSuccess(recordStatus);
    }
  }, [isRecordStatusSuccess, recordStatus]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSelectedParticipantLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSelectedParticipantLoading]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isBulkActivitiesResultLoading || isRecordStatusLoading,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isBulkActivitiesResultLoading, isRecordStatusLoading]);

  const selectedParticipantIds = useMemo(
    () =>
      filteredParticipants
        .filter((participant) => participant.isSelected)
        .map((participant) => participant.programId),
    [filteredParticipants],
  );

  const formattedActivities = useMemo(
    () =>
      activityList.map((activity) => ({
        activityType: activity.activityType,
        activityName: activity.activityTypeName,
        modality: activity.modality ?? "",
        actualDurationInMinutes: activity.actualDurationInMinutes ?? "",
        onBehalfOfCoachId: activity.onBehalfOfCoachId ?? "",
        topic: activity.topic?.map((t) => t.id) ?? [],
        notes: activity.notes ?? "",
        activityDate: activity.activityDate
          ? convertToUTCFormat(activity.activityDate, userCountryDateFormat)
          : "",
      })),
    [activityList],
  );
  useEffect(() => {
    if (isSelectedParticipantSuccess) {
      const { bulkProcessName } = getValues();
      setSummaryData({
        ...summaryData,
        bulkImportId: selectedParticipantData?.processId,
        totalRecords: selectedParticipantData?.totalRecords,
        processedRecords: selectedParticipantData?.processedRecords,
        status: selectedParticipantData?.status,
        processName: bulkProcessName,
      });
    }
  }, [isSelectedParticipantSuccess]);

  const handleOkClick = () => {
    const { bulkProcessName } = getValues();
    const payload: SelectedParticipantPayload = {
      activities: formattedActivities,
      participantIds: selectedParticipantIds,
      processName: bulkProcessName,
    };
    saveSelectedMutation(payload);
    setShowProceedModal(false);
  };
  useEffect(() => {
    //reset the selected participants and program template id on page load
    dispatch(setBulkActivitySelectedParticipants([]));
    dispatch(setBulkActivityProgramTemplateId([]));
    dispatch(setBulkActivityList([]));
  }, []);

  const onNextButtonClick = () => {
    if (selectedParticipants.length >= 2) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      setShowInformationModal(true);
    }
  };

  const handleBackButtonClick = () => {
    if (currentStep > 0) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const handleCloseInformation = () => {
    setShowInformationModal(false);
  };

  const handleNavigateBack = () => {
    navigate(PAGE_URLS.SEARCH_PROGRAM);
  };
  const onConfirmClick = () => {
    if (activityList.length >= 1) {
      setShowProceedModal(true);
    } else {
      setShowInformationModal(true);
    }
  };
  const handleCloseSomethingWentWrong = () => {
    setShowSomethingWrongModal(false);
  };

  const unProcessedParticipants =
    !isBulkActivitiesResultLoading &&
    !isBulkActivitiesResultError &&
    bulkActivitiesResult != null &&
    getValueOrZero(bulkActivitiesResult?.unProcessedParticipants) === 0;

  useEffect(() => {
    if (currentStep === 1 && summaryData.bulkImportId) {
      refetchRecordStatus({
        bulkImportId: summaryData.bulkImportId,
        category: BULK_CATEGORY_FOR_STATUS.bulkActivityProcess,
      });
    }
  }, [summaryData?.bulkImportId, currentStep, refreshCount]);

  const handleRefresh = () => {
    setRefreshCount((prev) => prev + 1);
  };

  const onAddBulkActivity = () => {
    searchRef.current?.handleResetButtonRef();
    setSummaryData({} as BulkUploadStatusResponse);
    setCurrentStep(0);
    setValue(CreateBulkActivityFormKey.bulkProcessName, "");
  };

  return (
    <CreateBulkActivityWrapper>
      <BulkInsertHeader />
      <CreateBulkActivityStepper
        currentStep={currentStep}
        isParticipantSelected={selectedParticipants.length >= 2}
        isActivityAdded={activityList.length > 0}
        isAllProcessed={
          isSelectedParticipantSuccess || isFromManageBulkProcessFlow
            ? unProcessedParticipants
            : false
        }
        status={summaryData.status}
      />
      <MuiTabs
        tabs={tabs}
        value={currentStep}
        tabsTestIds={[tabIdOne, tabIdTwo, tabIdThree]}
      >
        <MuiTabPanel
          panelId={`${pageId}-panel1`}
          value={currentStep}
          index={0}
          isPanelWithMount
          ariaLabelledById={tabIdOne}
        >
          <BulkActivitySearchParticipants
            ref={searchRef}
            onNextButtonClick={onNextButtonClick}
            currentStep={currentStep}
            handleNavigateBack={handleNavigateBack}
          />
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel2`}
          value={currentStep}
          index={1}
          isPanelWithMount
          ariaLabelledById={tabIdTwo}
        >
          {summaryData.status === SummaryStatus.PROCESSING ? (
            <BulkActivityProcess
              onNewBulkActivity={onAddBulkActivity}
              onRefresh={handleRefresh}
              summaryData={summaryData}
              pageId={pageId}
              handleNavigateBack={handleNavigateBack}
            />
          ) : (
            <FormProvider {...createBulkActivityMethods}>
              <BulkActivityDetails
                pageId={pageId}
                handleBackButtonClick={handleBackButtonClick}
                handleNavigateBack={handleNavigateBack}
                onConfirmBtnClick={handleSubmit(onConfirmClick)}
                currentStep={currentStep}
              />
            </FormProvider>
          )}
        </MuiTabPanel>
        <MuiTabPanel
          panelId={`${pageId}-panel3`}
          value={currentStep}
          index={2}
          isPanelWithMount
          ariaLabelledById={tabIdThree}
        >
          <BulkSummaryDetails
            currentStep={currentStep}
            summaryData={bulkActivitiesResult}
            onAddBulkActivity={onAddBulkActivity}
            pageId={pageId}
          />
        </MuiTabPanel>
      </MuiTabs>
      <InformationModal
        showModal={showInformationModal}
        handleModalClose={handleCloseInformation}
        handlePrimaryButtonClick={handleCloseInformation}
        message={informationMessage}
      />
      {showProceedModal && (
        <WarningConfirmationModal
          modalTitle={commonLabels.confirmation}
          labelContent={fetchLabel(
            bulkActivitySiteCoreKeys.activityTimeLine,
            bulkActivitySiteCoreKeys.addBulkActivityConfirmationText,
          )}
          show={showProceedModal}
          onClose={() => setShowProceedModal(false)}
          onPrimaryButtonClick={handleOkClick}
          primaryButtonTitle={commonLabels.yesProceed}
          secondaryButtonTitle={commonLabels.no}
        />
      )}
      <InformationModal
        showModal={showSomethingWrongModal}
        title={fetchLabel(
          bulkActivitySiteCoreKeys.activityTimeLine,
          bulkActivitySiteCoreKeys.somethingWentwrong,
        )}
        handleModalClose={handleCloseSomethingWentWrong}
        handlePrimaryButtonClick={handleCloseSomethingWentWrong}
        message={fetchLabel(
          bulkActivitySiteCoreKeys.activityTimeLine,
          bulkActivitySiteCoreKeys.serverErrorNotification,
        )}
      />
    </CreateBulkActivityWrapper>
  );
};

export default CreateBulkActivity;
